/**
=========================================================
* Soft UI Dashboard React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import axios from 'axios';  
import React from 'react';  
import Card from "@mui/material/Card";
import { useLocation, Link } from "react-router-dom";
import { FaShare, FaDollarSign, FaEdit, FaCopy, FaArrowLeft, FaRedo, FaExclamationTriangle, FaExternalLinkAlt } from "react-icons/fa";

// @mui material components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

// @mui material components
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import gr_logo from "assets/images/group.png";

// Styled Components
import { styled } from '@mui/material/styles';

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  color: '#495057',
  padding: theme.spacing(6, 0),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
  border: '1px solid #dee2e6',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.5,
  }
}));

const MatchCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  }
}));

const ActionButton = styled(Button)(({ theme, variant = 'primary' }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: theme.spacing(1, 2),
  minWidth: 'auto',
  ...(variant === 'primary' && {
    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
    }
  }),
  ...(variant === 'success' && {
    background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
    }
  }),
  ...(variant === 'warning' && {
    background: 'linear-gradient(45deg, #FF9800 30%, #F57C00 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #F57C00 30%, #FF9800 90%)',
    }
  }),
  ...(variant === 'info' && {
    background: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
    }
  }),
  ...(variant === 'error' && {
    background: 'linear-gradient(45deg, #F44336 30%, #D32F2F 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #D32F2F 30%, #F44336 90%)',
    }
  })
}));

const LoadingSpinner = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <Box textAlign="center">
      <CircularProgress size={60} sx={{ color: '#667eea', mb: 2 }} />
      <Typography variant="h6" color="textSecondary">
        Loading matches...
      </Typography>
    </Box>
  </Box>
);

const ErrorDisplay = ({ error, onRetry }) => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
    <Box textAlign="center" maxWidth={400}>
      <FaExclamationTriangle size={64} style={{ color: '#F44336', marginBottom: 16 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Error Loading Matches
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        {error}
      </Typography>
      <ActionButton
        variant="primary"
        startIcon={<FaRedo />}
        onClick={onRetry}
      >
        Try Again
      </ActionButton>
    </Box>
  </Box>
);

const BackButton = ({ onClick, children }) => (
  <Button
    variant="outlined"
    startIcon={<FaArrowLeft />}
    onClick={onClick}
    sx={{
      mb: 3,
      borderColor: 'rgba(0,0,0,0.1)',
      color: 'text.primary',
      '&:hover': {
        borderColor: 'rgba(0,0,0,0.2)',
        backgroundColor: 'rgba(0,0,0,0.02)',
      }
    }}
  >
    {children || 'Back to Group'}
  </Button>
);

const MatchItem = ({ match, onNotify, onEdit, onCopy, onAuction, showEdit }) => {
  const getStatusColor = (status, isDraft) => {
    if (isDraft === 'Draft') return 'warning';
    if (status === 'Full') return 'error';
    if (status === 'Open') return 'success';
    return 'default';
  };

  const getAuctionColor = (isAuction, endFlag) => {
    if (endFlag === '1') return 'error';
    if (isAuction === '1') return 'info';
    return 'dark';
  };

  // Convert is_draft from numeric to text
  const getDraftText = (isDraft) => {
    if (isDraft === '0' || isDraft === 0) return 'Published';
    if (isDraft === '1' || isDraft === 1) return 'Draft';
    return isDraft; // fallback for any other values
  };

             return ( 
    <MatchCard>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        {/* Left Side - Match Info */}
        <Box display="flex" alignItems="center" gap={2} flex={1}>
          <Avatar
            src={match.group_logo || gr_logo}
            alt={match.group_name}
            sx={{ 
              width: 48, 
              height: 48, 
              border: '2px solid #e0e0e0',
              borderRadius: '8px'
            }}
          />
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              <a href={match.invite_link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {match.series_name}
                <FaExternalLinkAlt size={14} style={{ color: '#1976d2' }} />
              </a>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {match.group_name}
            </Typography>
          </Box>
        </Box>

        {/* Right Side - Action Buttons */}
        {showEdit === '1' && match.is_admin === "1" && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {match.invite_code && (
              <ActionButton
                variant="success"
                startIcon={<FaShare />}
                onClick={() => onNotify(match.invite_code)}
                size="small"
              >
                Notify
              </ActionButton>
            )}

            <ActionButton
              variant={getAuctionColor(match.is_auction, match.end_flag)}
              startIcon={<FaDollarSign />}
              onClick={() => onAuction(match.group_id, match.series_id)}
              size="small"
            >
              Auction
            </ActionButton>

            <ActionButton
              variant="primary"
              component={Link}
              to={`/registration-requests?s=${match.series_id}`}
              size="small"
              sx={{ textDecoration: 'none' }}
            >
              Registrations
            </ActionButton>

            <ActionButton
              variant="info"
              startIcon={<FaEdit />}
              onClick={() => onEdit(match.group_id, match.series_id)}
              size="small"
            >
              Edit
            </ActionButton>

            <ActionButton
              variant="warning"
              startIcon={<FaCopy />}
              onClick={() => onCopy(match.group_id, match.series_id)}
              size="small"
            >
              Copy
            </ActionButton>
          </Box>
        )}
      </Box>

      {/* Bottom Section - Match Details */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={2} borderTop="1px solid #f0f0f0">
        <Box display="flex" gap={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" sx={{ fontSize: '10px', color: '#1976d2' }}>
                📅
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {match.match_date}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: '#fff3e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" sx={{ fontSize: '10px', color: '#f57c00' }}>
                🕐
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {match.match_time}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: '#e8f5e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="caption" sx={{ fontSize: '10px', color: '#388e3c' }}>
                👥
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {match.remaining_slots} / {match.total_participants}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={`${match.status_name} | ${getDraftText(match.is_draft)}`}
            color={getStatusColor(match.status_name, getDraftText(match.is_draft))}
            size="small"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.75rem',
              height: '24px'
            }}
          />
        </Box>
      </Box>
    </MatchCard>
  );
};

const MatchSection = ({ title, matches, onNotify, onEdit, onCopy, onAuction, showEdit, emptyMessage }) => (
  <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
      p: 3,
      color: '#495057',
      borderBottom: '1px solid #dee2e6'
    }}>
      <Typography variant="h5" fontWeight="bold">
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
        {matches?.length || 0} match{matches?.length !== 1 ? 'es' : ''} found
      </Typography>
    </Box>
    
    <Box p={3}>
      {matches && matches.length > 0 ? (
        <Box>
          {matches.map((match, index) => (
            <MatchItem
              key={index}
              match={match}
              onNotify={onNotify}
              onEdit={onEdit}
              onCopy={onCopy}
              onAuction={onAuction}
              showEdit={showEdit}
            />
          ))}
        </Box>
      ) : (
        <Box textAlign="center" py={6}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {emptyMessage}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No matches to display at the moment
          </Typography>
        </Box>
      )}
    </Box>
  </Card>
);
  
export default class Schedules extends React.Component {  
  constructor(props) {
    super(props);
    this.state = { 
      showModal: false,
      viewstatus: 'list',
      upcoming_schedules: [],
      past_schedules: [],
      loading: true,
      error: null
    };
  }

  componentDidMount() {  
    this.checkSessionAndLoadData();
  }

  checkSessionAndLoadData = async () => {
    try {
      this.setState({ loading: true, error: null });
      
      // Check session
      const sessionResponse = await axios.post(
        global.config.app.backend_api_url + 'login/sessionavailable',
        { lo: 1 },
        { withCredentials: true }
      );

      const user_id = sessionResponse.data.user_details?.user_id;
      if (!user_id || user_id === 'null') {
          window.location.replace('sign-in');
        return;
        }
        
      // Load match schedules
      await this.loadMatchSchedules();
        
    } catch (error) {
      console.error('Error loading data:', error);
      this.setState({ 
        error: 'Failed to load match schedules. Please try again.',
        loading: false 
      });
    }
  }

  loadMatchSchedules = async () => {
    try {
      let search1 = window.location.search;
      let params1 = new URLSearchParams(search1);
      let group_id1 = '';

      if (params1.get('g') != null) {
        group_id1 = atob(params1.get('g'));
      }
      
      const response = await axios.post(
        global.config.app.backend_api_url + 'matchschedule/getmatchschedules',
        { lo: 1, group_id: group_id1 },
        { withCredentials: true }
      );

      console.log('Match schedules response:', response.data);

      this.setState({
        upcoming_schedules: response.data.upcoming || [],
        past_schedules: response.data.past || [],
        loading: false,
        error: null
      });

      // Setup notifications if user details available
      if (response.data.user_details?.user_id) {
        this.callNotificationSubscribe(
          response.data.user_details.nai,
          response.data.user_details.user_id
        );
      }

    } catch (error) {
      console.error('Error loading match schedules:', error);
      this.setState({ 
        error: 'Failed to load match schedules. Please try again.',
        loading: false 
      });
    }
  }

  onClickNotify = async (invite_code) => {
    if (!invite_code) {
      alert("Invite code is missing!");
      return;
    }

    try {
      const response = await axios.post(
        global.config.app.backend_api_url + 'matchschedule/sendnotification',
        { invite_code },
        { withCredentials: true }
      );
      
      alert(response.data.message);
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification. Please try again.');
    }
  }

  handleEdit = (groupId, seriesId) => {
    const link_to_create = "/viewmembers";
    const querystring = "?id=" + btoa(groupId) + "&sid=" + btoa(seriesId);
    window.location.href = `${link_to_create}${querystring}`;
  }

  handleCopy = (groupId, seriesId) => {
    const link_to_create = "/viewmembers";
    const querystring = "?id=" + btoa(groupId) + "&csid=" + btoa(seriesId);
    window.location.href = `${link_to_create}${querystring}&state=copylink`;
  }

  handleAuction = (groupId, seriesId) => {
    const link_to_create = "/makeauction";
    const querystring = "?id=" + btoa(groupId) + "&sid=" + btoa(seriesId);
    window.location.href = `${link_to_create}${querystring}`;
  }

  handleBackToGroup = () => {
    let search1 = window.location.search;
    let params1 = new URLSearchParams(search1);
    let group_id1 = '';

    if (params1.get('g') != null) {
      group_id1 = atob(params1.get('g'));
    }

    if (group_id1) {
      const link_to_create = "/viewmembers";
      const querystring = "?id=" + btoa(group_id1);
      window.location.href = `${link_to_create}${querystring}`;
    }
  }

  callNotificationSubscribe(appid, userid) {
    if (!userid || !appid) return;
    
    const s = document.createElement("script");
    s.src = "https://notix.io/ent/current/enot.min.js";
    s.onload = (sdk) => {
        sdk.startInstall({
            appId: appid,
            loadSettings: true,
            disableConsoleDebug: true,
            user: userid
        });
    };
    document.head.append(s);
  }

  render() {  
    const { loading, error, upcoming_schedules, past_schedules } = this.state;
    
    // Get group ID from URL for back button
          let search1 = window.location.search;
          let params1 = new URLSearchParams(search1);
          let group_id1 = '';
    if (params1.get('g') != null) {
            group_id1 = atob(params1.get('g'));
          }

    if (loading) {
      return (
        <DashboardLayout>
          <DashboardNavbar />
          <SoftBox py={3}>
            <LoadingSpinner />
              </SoftBox>
          <Footer />
        </DashboardLayout>
      );
    }

    if (error) {
      return (
        <DashboardLayout>
          <DashboardNavbar />
          <SoftBox py={3}>
            <ErrorDisplay error={error} onRetry={this.checkSessionAndLoadData} />
              </SoftBox>
          <Footer />
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />
        
        <SoftBox py={3}>
          {/* Hero Section */}
          <HeroSection>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} 
                 justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Box>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  Match Schedule
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Manage your upcoming and past matches with ease
                </Typography>
              </Box>
              
              <Box display="flex" gap={2} mt={{ xs: 2, sm: 0 }}>
                {group_id1 && (
                  <Button
                    variant="outlined"
                    startIcon={<FaArrowLeft />}
                    onClick={this.handleBackToGroup}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.5)',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    Back to Group
                  </Button>
                )}
                
                <ActionButton
                  variant="primary"
                  startIcon={<FaRedo />}
                  onClick={this.checkSessionAndLoadData}
                >
                  Refresh
                </ActionButton>
              </Box>
            </Box>
          </HeroSection>

          {/* Match Sections */}
          <Box sx={{ spaceY: 4 }}>
            <MatchSection
              title="Upcoming Matches"
              matches={upcoming_schedules}
              onNotify={this.onClickNotify}
              onEdit={this.handleEdit}
              onCopy={this.handleCopy}
              onAuction={this.handleAuction}
              showEdit="1"
              emptyMessage="No upcoming matches scheduled"
            />
            
            <Box mt={4}>
              <MatchSection
                title="Past Matches"
                matches={past_schedules}
                onNotify={this.onClickNotify}
                onEdit={this.handleEdit}
                onCopy={this.handleCopy}
                onAuction={this.handleAuction}
                showEdit="1"
                emptyMessage="No past matches found"
              />
            </Box>
          </Box>
        </SoftBox>
        
      <Footer />
    </DashboardLayout>
  );
}
}  